import { useIp } from '@/components/ip-provider';
import { IS_USE_CENTIMETERS_SIZE_COUNTRY } from '@/constants/is-use-centimeters-size-country';
import { useCartContext } from '@/context/cart';
import { ADD_SIMPLE_MUTATION } from '@/hooks/ProductFullDetail/productFullDetail.gql';
import { findMatchingVariant } from '@/util/findMatchingProductVariant';
import { generateAddToCartImage } from '@/util/generate-add-to-cart-image';
import { gql, useMutation } from '@apollo/client';
import { useMemo, useState } from 'react';
import { OptionsSelectionsMap } from '../lib/get-optionSelections';
import { useProductFullDetailStore } from '../store/use-product-full-detail-store';
import type { OptionsRoot } from '../types/data';
import { ProductRoot } from '../types/product';

export const AmProtCustomizableDropDownOptionFragment = gql`
  fragment AmProtCustomizableDropDownOptionFragment on AmProtCustomizableDropDownOption {
    values {
      uid
      option_id
      value_index
      default_label
      label
      price
      sku
      swatch_data {
        type
        value
        thumbnail
      }
    }
  }
`;
export const AmProtCustomizableRadioOptionFragment = gql`
  fragment AmProtCustomizableRadioOptionFragment on AmProtCustomizableRadioOption {
    values {
      uid
      option_id
      value_index
      default_label
      label
      price
      sku
      swatch_data {
        type
        value
        thumbnail
      }
    }
  }
`;

export const ProductListFragment = gql`
  fragment ProductListFragment on Cart {
    id
    # eslint-disable-next-line @graphql-eslint/require-id-when-available
    items {
      uid
      # eslint-disable-next-line @graphql-eslint/require-id-when-available
      robes_gifts_customization {
        label
        value
      }
      select_product_image
      product {
        uid
        name
        sku
        url_key
        thumbnail {
          url(type: "thumbnail", width: 90, height: 132)
        }
        price {
          regularPrice {
            amount {
              currency
              value
            }
          }
        }

        media_gallery_entries {
          uid
          label
          position
          disabled
          file
        }
        media_gallery {
          label
          url(width: 90, height: 132)
          thumbnail: url(type: "thumbnail", width: 90, height: 132)
          image: url(type: "image", width: 692, height: 1016)
          position
          disabled
        }

        stock_status
        # eslint-disable-next-line @graphql-eslint/require-id-when-available
        ... on ConfigurableProduct {
          variants {
            attributes {
              uid
            }
            # eslint-disable-next-line @graphql-eslint/require-id-when-available
            product {
              uid
              thumbnail {
                url(type: "thumbnail", width: 90, height: 132)
              }
            }
          }
        }
        ampProtOption {
          items {
            uid
            label
            attribute_id
            type
            sort_order
            ...AmProtCustomizableDropDownOptionFragment
            ...AmProtCustomizableRadioOptionFragment
          }
        }
      }
      prices {
        price {
          currency
          value
        }
        total_item_discount {
          value
        }
      }
      quantity
      # eslint-disable-next-line @graphql-eslint/require-id-when-available
      ... on ConfigurableCartItem {
        # eslint-disable-next-line @graphql-eslint/require-id-when-available
        configurable_options {
          configurable_product_option_uid
          option_label
          configurable_product_option_value_uid
          value_label
        }
      }
      select_options {
        option_id
        option_value_id
      }
      custom_info {
        label
        value
      }
    }
  }
  ${AmProtCustomizableRadioOptionFragment}
  ${AmProtCustomizableDropDownOptionFragment}
`;

export const MiniCartFragment = gql`
  fragment MiniCartFragment on Cart {
    id
    total_quantity
    prices {
      subtotal_excluding_tax {
        currency
        value
      }
      subtotal_including_tax {
        currency
        value
      }
    }
    ...ProductListFragment
  }
  ${ProductListFragment}
`;

export const CartTriggerFragment = gql`
  fragment CartTriggerFragment on Cart {
    id
    total_quantity
  }
`;

export const ADD_CONFIGURABLE_MUTATION = gql`
  mutation addConfigurableProductToCart(
    $cartId: String!
    $quantity: Float!
    $sku: String!
    $parentSku: String!
    $product_image: String!
    $is_try_on: Int
    $try_on_order_item_id: ID
    $try_on_map_sku: String
    $robes_gifts_customization: [String]
    $color_price: Float
    $in_stock_sku: String
  ) {
    addConfigurableProductsToCart(
      input: {
        cart_id: $cartId
        cart_items: [
          {
            data: { quantity: $quantity, sku: $sku }
            parent_sku: $parentSku
            product_image: $product_image
            color_price: $color_price
            is_try_on: $is_try_on
            try_on_order_item_id: $try_on_order_item_id
            try_on_map_sku: $try_on_map_sku
            robes_gifts_customization: $robes_gifts_customization
            in_stock_sku: $in_stock_sku
          }
        ]
      }
    ) {
      cart {
        id
        # Update the cart trigger when adding an item.
        ...CartTriggerFragment
        # Update the mini cart when adding an item.
        ...MiniCartFragment
      }
    }
  }
  ${CartTriggerFragment}
  ${MiniCartFragment}
`;

export const useAddToCart = ({
  optionSelections,
  options,
  product,
  mediaGalleryEntries,
  form,
}: {
  product: ProductRoot;
  optionSelections: OptionsSelectionsMap;
  options: OptionsRoot;
  mediaGalleryEntries: any[];
  form: any;
}) => {
  const [addToCartStatus, setAddToCartStatus] = useState(false);

  const [addConfigurableProductToCart, { error: errorAddingConfigurableProduct, loading: isAddConfigurableLoading }] =
    useMutation(ADD_CONFIGURABLE_MUTATION);

  const [addSimpleProductToCart, { error: errorAddingSimpleProduct, loading: isAddSimpleLoading }] =
    useMutation(ADD_SIMPLE_MUTATION);

  const { watch } = form;
  const selectedSize = watch('Size');
  const customFieldsVisible = selectedSize?.label === 'customized_size';

  const [{ cartId }] = useCartContext();

  const { currentIp } = useIp();

  const trackAddToCartEvents = (product: ProductRoot, quantity: number) => {
    const currency =
      product?.price_range?.minimum_price?.final_price?.currency ||
      product?.price_range?.maximum_price?.final_price?.currency;
    const price =
      product?.price_range?.minimum_price?.final_price?.value ||
      product?.price_range?.maximum_price?.final_price?.value;

    window.dataLayer &&
      window.dataLayer.push({
        ecommerce: null,
      });
    window.dataLayer &&
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          currency: currency,
          value: price * quantity,
          items: [
            {
              item_name: product.name,
              item_id: product.sku,
              price: price,
              quantity: quantity,
            },
          ],
        },
      });

    // Pinterest
    window.pintrk &&
      window.pintrk('track', 'AddToCart', {
        value: price * quantity,
        order_quantity: quantity,
        currency: currency,
      });

    // fbq
    window.fbq &&
      window.fbq('track', 'AddToCart', {
        content_ids: [product.sku],
        content_name: product.name,
        content_type: 'product',
        contents: [
          {
            product_name: product.name,
            product_id: product.uid,
            product_category: null,
            product_price: price,
            product_brand: 'awbridal',
          },
        ],
        currency: currency,
        value: price * quantity,
      });
  };

  const attributeIdToValuesMap = useMemo(() => {
    const map = new Map();

    // For simple items, this will be an empty map.
    for (const { attribute_id, values } of options) {
      map.set(attribute_id, values);
    }

    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const selectedOptionsArray_sp = useMemo(() => {
    const selectedOptions_sp: any = [];

    optionSelections.forEach((value, key) => {
      const values = attributeIdToValuesMap.get(key);

      const selectedValue = values?.find((item: any) => item.value_index === value);

      if (selectedValue) {
        selectedOptions_sp.push({
          id: selectedValue.option_id,
          value_string: selectedValue.value_index,
        });
      }
    });

    return selectedOptions_sp;
  }, [attributeIdToValuesMap, optionSelections]);

  const optionCodes = useProductFullDetailStore(state => state.getOptionCode(product));

  const selectedOptionsArray = useMemo(() => {
    const selectedOptions: any = [];

    optionSelections.forEach((value, key) => {
      const values = attributeIdToValuesMap.get(key);

      const selectedValue = values?.find((item: any) => item.value_index === value);

      if (selectedValue) {
        selectedOptions.push(selectedValue.uid);
      }
    });

    return selectedOptions;
  }, [attributeIdToValuesMap, optionSelections]);

  // 选中颜色后显示当前颜色的价格
  const colorPrice = useMemo(() => {
    const colorOption = product?.ampProtOption?.items?.find(item => item.label.toLowerCase().includes('color'));

    if (!colorOption) {
      return null;
    }

    let selectedColor;

    for (const key of Array.from(optionSelections.keys())) {
      const value = optionSelections.get(key);

      if (key === colorOption.attribute_id) {
        selectedColor = colorOption.values.find(temp => temp.value_index === value);
      }
    }

    if (selectedColor) {
      return selectedColor.price;
    }

    return null;
  }, [optionSelections, product?.ampProtOption?.items]);

  const handleAddToCart = async ({
    allFormData,
    product,
    in_stock_sku,
    showMiniCart = true,
    afterSubmit,
    clearCheckCustomSizeRecord,
  }: {
    allFormData: any;
    product: ProductRoot;
    in_stock_sku: string;
    showMiniCart?: boolean;
    afterSubmit?: any;
    clearCheckCustomSizeRecord?: () => void;
  }) => {
    let isSuccess = false;
    const productImage = generateAddToCartImage({ mediaGalleryEntries });
    const { quantity } = allFormData;

    const { robes_gifts_customization } = allFormData;

    const {
      bust,
      waist,
      hip,
      hollowToFloor: hollowtoFloor,
      height,
      extraLength: extralength,
      hollowtoHem,
    } = allFormData || {};
    const { agreement } = allFormData || {};

    const { pocket: pockets, backDetail: back_detail, length } = allFormData || {};

    if (product) {
      try {
        trackAddToCartEvents(product, quantity);
      } catch (error) {
        console.error('跟踪添加到购物车事件时出错:', error);
      }
    }

    try {
      if (product?.__typename === 'SimpleProduct') {
        const variables: any = {
          cartId,
          sku: product.sku,
          quantity,
          customizable_options: selectedOptionsArray_sp,
          bust: bust,
          category_id: 0,
          category_name: product?.categories[product?.categories.length - 1].name,
          customize_price: 0,
          customsize: customFieldsVisible ? 'on' : '',
          data_sku: '',
          dresseshemline: product?.show_attribute.find(item => item.label === 'Hemline')?.value,
          extralength: extralength,
          height: height,
          hip: hip,
          // hollowtoFloor: hollowtoFloor,
          // hollowtoHem: hollowtoFloor,
          length: length,
          pockets: pockets,
          back_detail: back_detail,
          product_color: '',
          product_image: productImage,
          color_price: colorPrice,
          shipingtypes: product?.show_attribute.find(item => item.label === 'Shipping Type')?.id,
          agreement: agreement ? 'no' : '',
          unit: IS_USE_CENTIMETERS_SIZE_COUNTRY ? 'cm' : 'inch',
          waist: waist,
          watermark: 1,
        };

        if (product?.shipping_type === 'Dresses') {
          variables.hollowtoHem = hollowtoHem;
        } else {
          variables.hollowtoFloor = hollowtoFloor;
        }

        await addSimpleProductToCart({
          variables,
        });
      } else {
        const item = findMatchingVariant({
          optionCodes,
          optionSelections,
          variants: product.variants,
        });

        const variables: any = {
          cartId,
          product: {
            ...product,
            selected_options: selectedOptionsArray,
          },
          quantity,
          sku: item.product.sku,
          parentSku: product.sku,
          product_image: productImage,
          in_stock_sku: in_stock_sku,
          color_price: colorPrice,
          is_try_on: product.is_try_on,
          try_on_map_sku: product.try_on_map_sku,
        };

        if (robes_gifts_customization) {
          variables.robes_gifts_customization = [`letter@@${robes_gifts_customization}`];
        }

        await addConfigurableProductToCart({
          variables,
        });
      }

      setAddToCartStatus(true);
      isSuccess = true;

      setTimeout(() => {
        setAddToCartStatus(false);
      }, 3000);
      clearCheckCustomSizeRecord?.();

      if (showMiniCart) {
        const cartAddEvent = new Event('CART_ADD_ITEM', {
          bubbles: true,
        });

        window.document.dispatchEvent(cartAddEvent);
      }

      try {
        await fetch('/api/pinterest-add-to-cart', {
          method: 'POST',
          body: JSON.stringify({
            product,
            productPrice: product?.price_range?.maximum_price,
            quantity,
            ip: currentIp,
          }),
        });
      } catch (e) {
        console.log('Error', e);
      }
    } catch (error) {
      setAddToCartStatus(false);
      isSuccess = false;
      clearCheckCustomSizeRecord?.();
      console.error(error);
    }

    try {
      if (isSuccess) {
        afterSubmit && afterSubmit();
      }
    } catch {}
  };

  return {
    addToCartStatus,
    handleAddToCart,
    errorMessage: errorAddingConfigurableProduct || errorAddingSimpleProduct,
    isAdding: isAddConfigurableLoading || isAddSimpleLoading,
    selectedOptionsArray,
  };
};
